// WorkTabs -- one tabbed section for the two placements of the open session
// among the org's work. Related work looks BACKWARD to prior sessions that
// share file evidence with this one; Initiatives looks OUTWARD to the
// tenant-wide bodies of work the census says this session participates in.
// The questions are close enough that stacking both sections doubled the
// panel's height for one reading, so they share a tab strip.
//
// Both panels stay mounted across flips: each read runs once per session and
// row-expansion state survives switching away and back. A panel that reports
// itself hidden loses its tab (a tenant with no census gets a plain Related
// work header); when both are hidden the section vanishes entirely, matching
// the old stacked behavior. Tab labels carry the ready row count so a flip
// is informed, not exploratory.
import { useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import InitiativesPanel, { initiativesTooltip } from "./InitiativesPanel.jsx";
import RelatedWorkPanel, { relatedWorkTooltip } from "./RelatedWorkPanel.jsx";

const TAB_BASE =
    "text-[11px] leading-[1.08] tracking-[0.08em] uppercase bg-transparent border-0 p-0";

function shown(state) {
    return state.status !== "hidden" && state.status !== "idle";
}

export default function WorkTabs({ trace, onOpenTrace }) {
    const [tab, setTab] = useState("related");
    const [related, setRelated] = useState({ status: "idle" });
    const [initiatives, setInitiatives] = useState({ status: "idle" });

    const tabs = [
        shown(related) && {
            key: "related",
            label: "Related work",
            count: related.status === "ready" ? related.suggestions.length : null,
            tooltip: relatedWorkTooltip(related),
        },
        shown(initiatives) && {
            key: "initiatives",
            label: "Initiatives",
            count: initiatives.status === "ready" ? initiatives.initiatives.length : null,
            tooltip: initiativesTooltip(),
        },
    ].filter(Boolean);

    // Falls back to whichever tab survives when the selected panel reports
    // itself hidden (e.g. the census disappears between sessions).
    const active = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;
    const activeTab = tabs.find((t) => t.key === active) || null;

    // The container hides rather than unmounts when no panel is shown: the
    // panels must stay in the tree to run their reads and report back.
    return (
        <div
            className={
                tabs.length
                    ? "grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0"
                    : "hidden"
            }
        >
            {tabs.length > 0 && (
                <div className="flex items-baseline gap-[10px]">
                    {tabs.map((t) =>
                        tabs.length === 1 ? (
                            <span key={t.key} className={`${TAB_BASE} text-[color:var(--muted)]`}>
                                {t.label}
                            </span>
                        ) : (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                aria-pressed={t.key === active}
                                className={[
                                    TAB_BASE,
                                    "cursor-pointer transition-colors",
                                    t.key === active
                                        ? "text-[color:var(--ink)]"
                                        : "text-[color:var(--muted-soft)] hover:text-[color:var(--muted)]",
                                ].join(" ")}
                            >
                                {t.label}
                                {t.count !== null ? ` · ${t.count}` : ""}
                            </button>
                        ),
                    )}
                    {activeTab && (
                        <HoverTooltip wide placement="top" label={activeTab.tooltip}>
                            <span className="cursor-help text-[10px] leading-[1.2] text-[color:var(--muted-soft)] underline decoration-dotted underline-offset-2">
                                details
                            </span>
                        </HoverTooltip>
                    )}
                </div>
            )}
            <div className={active === "related" ? "grid min-w-0" : "hidden"}>
                <RelatedWorkPanel
                    trace={trace}
                    onOpenTrace={onOpenTrace}
                    variant="tab"
                    onStatus={setRelated}
                />
            </div>
            <div className={active === "initiatives" ? "grid min-w-0" : "hidden"}>
                <InitiativesPanel
                    trace={trace}
                    variant="tab"
                    onStatus={setInitiatives}
                    onOpenTrace={onOpenTrace}
                />
            </div>
        </div>
    );
}
